import { ld } from "@/lib/theme/light-dark";
import {
  ArrowLeft,
  ArrowUpRight,
  ClipboardCheck,
  ClipboardList,
  FileSignature,
  Gauge,
  Inbox,
  LayoutGrid,
  Settings,
  SquarePen,
} from "lucide-react";
/**
 * Formulare module — the form-builder screen of the marketing HERO app window.
 * Layout: a 220px sidebar (Formulare / Einsendungen + "Meine Formulare" list)
 * and a full-bleed builder (topbar with Bauen/Einstellungen/Einsendungen + edit
 * toggle, a field palette, and the "Seite 1" canvas), plus a floating phone
 * overlay showing the filled form. 1:1 with the prototype (`isFormulare`
 * sidebar source line 588, content line 1448, phone overlay line 2006).
 */
import { useTranslations } from "next-intl";
import type { ReactNode } from "react";
import { H, MODULE_HEIGHT, SIDEBAR_WIDTH } from "../../hero-tokens";
import type { ModuleProps } from "../module-registry";
import { ModuleFormulareCanvas } from "./module-formulare-canvas";
import { ModuleFormularePalette } from "./module-formulare-palette";

/** A plain (inactive) sidebar nav row: grey icon chip + label. */
function SidebarItem(props: {
  readonly icon: ReactNode;
  readonly label: string;
}): ReactNode {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 9,
        padding: "7px 8px",
        borderRadius: 8,
      }}
    >
      <span
        style={{
          width: 26,
          height: 26,
          borderRadius: 7,
          background: ld("#F1F2F4", "#2b2e32"),
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: H.inkFaint,
          flexShrink: 0,
        }}
      >
        {props.icon}
      </span>
      <span
        style={{
          fontSize: 13,
          color: H.ink,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {props.label}
      </span>
    </div>
  );
}

/** The Formulare sidebar (top nav rows + "Meine Formulare" list). */
function FormulareSidebar(): ReactNode {
  const t = useTranslations("marketing.moduleFormulare");
  return (
    <div
      style={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        borderRight: `1px solid ${ld("#EEF0F3", "#2c2f33")}`,
        background: ld("#FBFBFC", "#1d1f23"),
        padding: "16px 12px",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 4px 14px",
        }}
      >
        <span style={{ fontSize: 16, fontWeight: 700 }}>{t("formulare")}</span>
        <span
          style={{
            color: ld("rgba(11,15,25,0.4)", "rgba(250,250,250,0.4)"),
            display: "inline-flex",
            cursor: "pointer",
          }}
        >
          <SquarePen width={15} height={15} strokeWidth={1.8} />
        </span>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <SidebarItem
          icon={<ClipboardList width={14} height={14} strokeWidth={1.8} />}
          label={t("formulare")}
        />
        <SidebarItem
          icon={<Inbox width={14} height={14} strokeWidth={1.8} />}
          label={t("einsendungen")}
        />
      </div>

      <div
        style={{
          fontFamily: H.label,
          fontSize: 10,
          letterSpacing: "0.12em",
          color: H.inkGhost,
          textTransform: "uppercase",
          padding: "18px 6px 9px",
        }}
      >
        {t("meineFormulare")}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Active: Übergabeformular */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 9,
            padding: "7px 8px",
            borderRadius: 8,
            background: H.accentSoft,
          }}
        >
          <span
            style={{
              width: 26,
              height: 26,
              borderRadius: 7,
              background: ld("#fff", "#181b1f"),
              border: `1px solid ${H.accentBorder}`,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              color: H.accent,
              flexShrink: 0,
            }}
          >
            <FileSignature width={14} height={14} strokeWidth={1.9} />
          </span>
          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: H.accent,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {t("uebergabeformular")}
          </span>
        </div>
        <SidebarItem
          icon={<Gauge width={14} height={14} strokeWidth={1.8} />}
          label={t("zaehlerstandAblesen")}
        />
        <SidebarItem
          icon={<ClipboardCheck width={14} height={14} strokeWidth={1.8} />}
          label={t("wohnungsbegehung")}
        />
      </div>
    </div>
  );
}

/** The builder topbar segmented tab (Bauen / Einstellungen / Einsendungen). */
function BuilderTabs(): ReactNode {
  const t = useTranslations("marketing.moduleFormulare");
  return (
    <div
      style={{
        marginLeft: 16,
        display: "flex",
        alignItems: "center",
        gap: 2,
        background: ld("#F3F3EF", "#2a2d31"),
        border: `1px solid ${ld("#EAEAE5", "#323539")}`,
        borderRadius: 9,
        padding: 3,
      }}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontSize: 12.5,
          fontWeight: 600,
          color: H.ink,
          background: ld("#fff", "#181b1f"),
          borderRadius: 6,
          padding: "5px 11px",
          boxShadow: `0 1px 2px ${ld("rgba(11,15,25,0.10)", "rgba(0,0,0,0.2)")}`,
        }}
      >
        <LayoutGrid width={14} height={14} strokeWidth={1.9} />
        {t("bauen")}
      </span>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontSize: 12.5,
          fontWeight: 500,
          color: H.inkFaint,
          padding: "5px 11px",
        }}
      >
        <Settings width={14} height={14} strokeWidth={1.8} />
        {t("einstellungen")}
      </span>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontSize: 12.5,
          fontWeight: 500,
          color: H.inkFaint,
          padding: "5px 11px",
        }}
      >
        <Inbox width={14} height={14} strokeWidth={1.8} />
        {t("einsendungen")}
        <ArrowUpRight
          width={12}
          height={12}
          strokeWidth={1.8}
          style={{ opacity: 0.6 }}
        />
      </span>
    </div>
  );
}

/** The full builder content (full-bleed: topbar + palette + canvas). */
function FormulareBuilder(): ReactNode {
  const t = useTranslations("marketing.moduleFormulare");
  return (
    <div
      style={{
        margin: "-20px -22px",
        height: MODULE_HEIGHT,
        background: ld("#FAFAF7", "#1e2125"),
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Topbar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "9px 16px",
          borderBottom: `1px solid ${ld("#EAEAE5", "#323539")}`,
          flexShrink: 0,
          background: ld("#fff", "#181b1f"),
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: 13,
            color: ld("rgba(11,15,25,0.55)", "rgba(250,250,250,0.55)"),
            cursor: "pointer",
          }}
        >
          <ArrowLeft width={16} height={16} strokeWidth={1.8} />
          {t("zurueck")}
        </span>
        <span style={{ width: 1, height: 16, background: ld("#E4E4DF", "#373a3e") }} />
        <span style={{ fontSize: 14, fontWeight: 600, color: H.ink }}>
          {t("uebergabeformular")}
        </span>
        <BuilderTabs />
        {/* Edit-mode toggle (static, On) */}
        <span
          style={{
            marginLeft: "auto",
            display: "inline-flex",
            alignItems: "center",
            gap: 9,
            fontSize: 12.5,
            fontWeight: 500,
            color: H.inkMute,
            cursor: "pointer",
          }}
        >
          {t("bearbeitungsmodus")}
          <span
            style={{
              width: 34,
              height: 20,
              borderRadius: 999,
              background: H.accent,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "flex-end",
              padding: 2,
              boxSizing: "border-box",
            }}
          >
            <span
              style={{
                width: 16,
                height: 16,
                borderRadius: "50%",
                background: ld("#fff", "#181b1f"),
                boxShadow: `0 1px 2px ${ld("rgba(0,0,0,0.25)", "rgba(0,0,0,0.425)")}`,
              }}
            />
          </span>
        </span>
      </div>

      {/* Palette + canvas */}
      <div
        style={{ flex: 1, minHeight: 0, display: "flex", position: "relative" }}
      >
        <ModuleFormularePalette />
        <ModuleFormulareCanvas />
      </div>
    </div>
  );
}

/** The Formulare module — sidebar + builder content + floating phone overlay. */
export function ModuleFormulare(_props: ModuleProps): ReactNode {
  return (
    <div style={{ display: "flex", height: MODULE_HEIGHT }}>
      <FormulareSidebar />
      <div
        style={{
          flex: 1,
          minWidth: 0,
          background: ld("#fff", "#181b1f"),
          padding: "20px 22px",
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        <FormulareBuilder />
      </div>
    </div>
  );
}
